import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const PublicLanding = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const legalCategories = [
    { name: 'Criminal Law', icon: 'Gavel', description: 'Defense and prosecution matters' },
    { name: 'Family Law', icon: 'Users', description: 'Divorce, custody, adoption' },
    { name: 'Corporate Law', icon: 'Building', description: 'Business and commercial matters' },
    { name: 'Property Law', icon: 'Home', description: 'Real estate and land disputes' },
    { name: 'Employment Law', icon: 'Briefcase', description: 'Workplace and labor issues' },
    { name: 'Contract Law', icon: 'FileText', description: 'Agreements and negotiations' }
  ];

  const features = [
    {
      icon: 'Search',
      title: 'Find Your Lawyer',
      description: 'Get matched with qualified lawyers based on your specific legal needs and location'
    },
    {
      icon: 'Shield',
      title: 'Secure Communication',
      description: 'Communicate safely with your lawyer through our encrypted messaging system'
    },
    {
      icon: 'Clock',
      title: '24/7 Access',
      description: 'Access your case information and communicate with your lawyer anytime, anywhere'
    },
    {
      icon: 'Award',
      title: 'Verified Lawyers',
      description: 'All lawyers on our platform are verified and licensed to practice in Kenya'
    }
  ];


  const handleGetStarted = () => {
    navigate('/client-registration');
  };

  const handleLawyerLogin = () => {
    navigate('/login');
  };

  const handlePitchDeck = () => {
    navigate('/pitch-deck');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-surface/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Scale" size={24} color="white" />
              </div>
              <div>
                <span className="text-xl font-heading font-bold text-primary">LawDesk </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={handlePitchDeck}
                className="flex items-center space-x-1.5 text-text-secondary hover:text-primary transition-smooth text-sm border border-border hover:border-primary rounded-md px-3 py-1.5"
              >
                <Icon name="TrendingUp" size={15} />
                <span>Investor Deck</span>
              </button>
              <button
                onClick={handleLawyerLogin}
                className="text-text-secondary hover:text-text-primary transition-smooth"
              >
                User Login
              </button>
              <button
                onClick={handleGetStarted}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-smooth"
              >
                Get Legal Help
              </button>
            </div>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-text-primary mb-6">
              Find the Right Lawyer for Your Legal Matter
            </h1>
            <p className="text-xl text-text-secondary mb-8 max-w-3xl mx-auto">
              Connect with qualified, verified lawyers across Kenya. Get expert legal help for any matter - from family disputes to business contracts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetStarted}
                className="bg-primary text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-primary-700 transition-smooth">

                Get Started - It's Free
              </button>
              <button className="border-2 border-primary text-primary px-8 py-4 rounded-lg text-lg font-medium hover:bg-primary-50 transition-smooth">
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* Pitch Deck Banner */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary-900 to-primary-700">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="TrendingUp" size={22} color="white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">Investors &amp; Partners</p>
              <p className="text-primary-200 text-xs">Explore our vision, traction, and growth strategy</p>
            </div>
          </div>
          <button
            onClick={handlePitchDeck}
            className="flex items-center space-x-2 bg-white text-primary px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary-50 transition-smooth flex-shrink-0"
          >
            <Icon name="Presentation" size={16} />
            <span>View Pitch Deck</span>
          </button>
        </div>
      </section>
      {/* Legal Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-bold text-text-primary mb-4">
              Legal Services We Cover
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Whatever your legal issue, we have experienced lawyers ready to help
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {legalCategories?.map((category, index) =>
            <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border hover:shadow-lg transition-smooth">
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={category?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                  {category?.name}
                </h3>
                <p className="text-text-secondary">
                  {category?.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Features */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-bold text-text-primary mb-4">
              Why Choose LawDesk KE?
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              We make it easy to find and work with the right lawyer for your needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features?.map((feature, index) =>
            <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name={feature?.icon} size={32} color="white" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                  {feature?.title}
                </h3>
                <p className="text-text-secondary">
                  {feature?.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-bold text-text-primary mb-4">
              How It Works
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Getting legal help is simple with our three-step process
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                Describe Your Legal Issue
              </h3>
              <p className="text-text-secondary">
                Tell us about your legal matter and we'll match you with qualified lawyers
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                Get Matched with Lawyers
              </h3>
              <p className="text-text-secondary">
                Review profiles and choose the lawyer that best fits your needs and budget
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                Start Working Together
              </h3>
              <p className="text-text-secondary">
                Communicate securely and track your case progress through our platform
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Ready to Get Legal Help?
          </h2>
          <p className="text-primary-100 text-lg mb-8">
            Join thousands of Kenyans who have found the right lawyer through LawDesk KE
          </p>
          <button
            onClick={handleGetStarted}
            className="bg-white text-primary px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-smooth">

            Get Started Now
          </button>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-surface border-t border-border py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Scale" size={20} color="white" />
            </div>
            <span className="text-lg font-heading font-bold text-primary">LawDesk</span>
          </div>
          <p className="text-text-secondary">
            © {new Date()?.getFullYear()} LawDesk KE. Making legal help accessible to all Kenyans.
          </p>
        </div>
      </footer>
    </div>
  );

};

export default PublicLanding;